import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useDeleteMe, useDeleteMePreflight, useUserProfile } from '@repo/queries';
import { useSession } from '@repo/api-client/react';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';

export function DeleteAccountScreen() {
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const { preflight, loading } = useDeleteMePreflight();
  const { deleteMe, loading: deleting } = useDeleteMe();
  const { signOut } = useSession();
  const { toast } = useToast();

  const [confirm, setConfirm] = useState('');

  if (loading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="mt-6 h-72 rounded-xl" />
      </Page>
    );
  }

  const blocking = preflight?.blockingOrgs ?? [];
  const solo = preflight?.soloOrgs ?? [];

  const blocked = blocking.length > 0;
  const armed =
    !blocked &&
    profile?.email &&
    confirm.toLowerCase() === profile.email.toLowerCase();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!profile?.email || blocked) return;
    try {
      await deleteMe({ confirmEmail: profile.email });
      await signOut();
      toast({ title: 'Account deleted', variant: 'success' });
      navigate('/sign-in');
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <Button variant="link" size="sm" asChild className="-ml-2 mb-4">
        <Link to="/settings/account">
          <ArrowLeft className="size-4" /> Back to account
        </Link>
      </Button>
      <PageHeader
        title="Delete account"
        description="Permanently remove your account and any solo workspaces."
      />

      {blocked ? (
        <Card className="border-[var(--danger-soft)]">
          <CardHeader>
            <CardTitle className="text-base">Resolve these workspaces first</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-muted-foreground">
              You're the only owner of {blocking.length} workspace
              {blocking.length === 1 ? '' : 's'} with other members. Transfer ownership
              or remove the other members before deleting your account.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {blocking.map((o) => (
                <li key={o.id}>
                  <Link to={`/settings/businesses/${o.id}`} className="hover:underline">
                    {o.name}
                  </Link>{' '}
                  ({o.memberCount} members, {o.ownerCount} owner
                  {o.ownerCount === 1 ? '' : 's'})
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[var(--danger-soft)] max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-destructive">
              <AlertTriangle className="size-4" /> This cannot be undone
            </CardTitle>
          </CardHeader>
          <CardContent>
            {solo.length > 0 ? (
              <Alert variant="danger" className="mb-4">
                <p className="text-sm">
                  Deleting your account will permanently remove these workspaces (no
                  other members):
                </p>
                <ul className="mt-2 list-disc pl-5 text-sm">
                  {solo.map((o) => (
                    <li key={o.id}>{o.name}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Field
                label={`Type your email to confirm (${profile?.email ?? ''})`}
                htmlFor="confirm"
                required
              >
                <Input
                  id="confirm"
                  type="email"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </Field>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="danger"
                  loading={deleting}
                  disabled={!armed}
                >
                  Delete my account
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </Page>
  );
}
